import { Link } from 'react-router-dom';
import { ArrowUpRight, Play, ArrowUp, ArrowRight, ArrowDown, ArrowLeft } from 'lucide-react';
import type { Direction } from '../../../lib/types';
import { scoreTo100 } from '../../../lib/exitScoring';
import { getScoreLabel, getScoreTextColor } from '../../../lib/constants';
import IsometricGarage from './IsometricGarage';

interface DirScore {
  direction: Direction;
  score: number; // 0-10
  label: string;
}

// Fallback values approximate the mockup; replaced by real venue data when available.
const FALLBACK: Record<Direction, number> = { N: 8.2, E: 7.6, S: 6.5, W: 9.1 };

const ARROW_ICON: Record<Direction, typeof ArrowUp> = {
  N: ArrowUp,
  E: ArrowRight,
  S: ArrowDown,
  W: ArrowLeft,
};

// Position of each compass node around the garage (absolute %, relative to box)
const POS: Record<Direction, string> = {
  N: 'top-0 left-1/2 -translate-x-1/2',
  E: 'top-1/2 right-0 -translate-y-1/2',
  S: 'bottom-0 left-1/2 -translate-x-1/2',
  W: 'top-1/2 left-0 -translate-y-1/2',
};

function CompassNode({ d }: { d: DirScore }) {
  const Arrow = ARROW_ICON[d.direction];
  const color = getScoreTextColor(d.score);
  const display = scoreTo100(d.score);
  const label = getScoreLabel(d.score);
  return (
    <div className={`absolute ${POS[d.direction]} flex flex-col items-center z-10`}>
      <div
        // Larger, higher-contrast nodes so the score reads clearly at 390px.
        className="w-16 h-16 sm:w-14 sm:h-14 rounded-full bg-white shadow-md border-2 border-gray-100 flex flex-col items-center justify-center"
        style={{ color }}
      >
        <div className="flex items-center gap-0.5">
          <span className="text-[11px] sm:text-[10px] font-bold">{d.direction}</span>
          <Arrow className="w-3.5 h-3.5 sm:w-3 sm:h-3" strokeWidth={2.5} />
        </div>
        <span className="text-lg sm:text-base font-extrabold leading-none tabular-nums">{display}</span>
      </div>
      <span className="mt-1 text-[11px] sm:text-[10px] font-bold" style={{ color }}>
        {label}
      </span>
    </div>
  );
}

export default function HeroClearance({ scores }: { scores?: Record<Direction, DirScore> }) {
  const dirs: Direction[] = ['N', 'E', 'S', 'W'];
  const data: Record<Direction, DirScore> = (() => {
    if (scores) return scores;
    const out = {} as Record<Direction, DirScore>;
    for (const d of dirs) out[d] = { direction: d, score: FALLBACK[d], label: getScoreLabel(FALLBACK[d]) };
    return out;
  })();

  return (
    <section className="bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* LEFT */}
          <div>
            <p
              className="text-xs font-bold uppercase tracking-wider mb-5"
              style={{ color: '#17A24A' }}
            >
              The fastest way home starts before you park.
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-gray-900 leading-[1.05]">
              Choose the fastest{' '}
              <span style={{ color: '#17A24A' }}>way out.</span>
            </h1>
            <p className="mt-6 text-lg text-gray-500 max-w-lg leading-relaxed">
              ParkingRank scores every garage exit by direction so you can avoid
              congestion before you park.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-5">
              <Link
                to="/venues"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: '#17A24A' }}
              >
                Find parking
                <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
              </Link>
              <Link
                to="/methodology"
                className="inline-flex items-center gap-2 text-base font-semibold text-gray-700 hover:text-gray-900"
              >
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-gray-300">
                  <Play className="w-3.5 h-3.5 fill-current" />
                </span>
                How it works
              </Link>
            </div>
          </div>

          {/* RIGHT — directional score visualization */}
          <div className="flex justify-center">
            {/* Fluid square that fills the mobile column (capped) so the compass never
                shrinks illegibly or clips its edge nodes; fixed, roomy size on desktop. */}
            <div className="relative w-full max-w-[300px] aspect-square sm:w-[400px] sm:max-w-none sm:aspect-auto sm:h-[400px]">
              {/* dotted connectors — slightly heavier so they read on small screens */}
              <svg className="absolute inset-0 w-full h-full" aria-hidden="true">
                <line x1="50%" y1="50%" x2="50%" y2="9%" stroke="#cbd5e1" strokeWidth="2" strokeDasharray="3 5" />
                <line x1="50%" y1="50%" x2="91%" y2="50%" stroke="#cbd5e1" strokeWidth="2" strokeDasharray="3 5" />
                <line x1="50%" y1="50%" x2="50%" y2="91%" stroke="#cbd5e1" strokeWidth="2" strokeDasharray="3 5" />
                <line x1="50%" y1="50%" x2="9%" y2="50%" stroke="#cbd5e1" strokeWidth="2" strokeDasharray="3 5" />
              </svg>

              {/* garage center */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 sm:w-44 sm:h-44 flex items-center justify-center">
                <IsometricGarage className="w-full h-full" />
              </div>

              {dirs.map((d) => (
                <CompassNode key={d} d={data[d]} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
